import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash, PencilSimple, Buildings, Users, Shield } from '@phosphor-icons/react';
import { API } from './adminConstants';
import { CsvExportButton, CsvImportButton } from './CsvHelpers';

export function DepartmentManagement() {
  const { getAuthHeader } = useAuth();
  const [depts, setDepts] = useState([]);
  const [managers, setManagers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newDept, setNewDept] = useState({ name: '', description: '', manager_id: '' });
  const [editDept, setEditDept] = useState(null);

  const load = async () => {
    const headers = getAuthHeader();
    const [dRes, uRes] = await Promise.all([
      axios.get(`${API}/admin/departments`, { headers }),
      axios.get(`${API}/admin/users`, { headers })
    ]);
    setDepts(dRes.data);
    setManagers(uRes.data.filter(u => ['admin', 'hr_admin', 'director', 'manager'].includes(u.role) || u.is_manager));
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/departments`, { ...newDept, manager_id: newDept.manager_id || null }, { headers: getAuthHeader() });
      toast.success('Department created');
      setShowCreate(false);
      setNewDept({ name: '', description: '', manager_id: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleUpdate = async (id, data) => {
    try {
      await axios.put(`${API}/admin/departments/${id}`, data, { headers: getAuthHeader() });
      toast.success('Updated');
      setEditDept(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/departments/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="department-management">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Manage Departments</h1><p className="text-slate-500 text-sm">Create and manage company departments linked to holiday approvals</p></div>
        <div className="flex gap-2">
          <CsvExportButton endpoint="/csv/export/departments" label="Export" />
          <CsvImportButton endpoint="/csv/import/departments" label="Import" onSuccess={load} />
          <Button onClick={() => setShowCreate(true)} className="bg-lime-600 hover:bg-lime-700" data-testid="create-dept-btn"><Plus size={15} className="mr-1" />Add Department</Button>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6" data-testid="create-dept-modal">
            <h3 className="font-semibold text-slate-900 mb-4">Create Department</h3>
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input value={newDept.name} onChange={e => setNewDept({...newDept, name: e.target.value})} data-testid="new-dept-name" /></div>
              <div><Label className="text-xs">Description</Label><Textarea value={newDept.description} onChange={e => setNewDept({...newDept, description: e.target.value})} rows={2} /></div>
              <div><Label className="text-xs">Department Manager</Label>
                <Select value={newDept.manager_id || '_none'} onValueChange={v => setNewDept({...newDept, manager_id: v === '_none' ? '' : v})}>
                  <SelectTrigger data-testid="new-dept-manager"><SelectValue placeholder="Select manager" /></SelectTrigger>
                  <SelectContent><SelectItem value="_none">None</SelectItem>{managers.map(m => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleCreate} className="flex-1 bg-lime-600 hover:bg-lime-700" data-testid="confirm-create-dept">Create</Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {depts.map(d => (
          <div key={d.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`dept-card-${d.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-lime-50"><Buildings size={22} className="text-lime-600" /></div>
                <div><h3 className="font-semibold text-slate-900">{d.name}</h3>{d.description && <p className="text-xs text-slate-500 mt-0.5">{d.description}</p>}</div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditDept(d)} className="h-7 w-7 p-0"><PencilSimple size={14} /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(d.id)} className="h-7 w-7 p-0 text-red-500"><Trash size={14} /></Button>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Users size={14} />{d.member_count} members</span>
              {d.manager_name && <span className="flex items-center gap-1"><Shield size={14} />{d.manager_name}</span>}
            </div>
          </div>
        ))}
        {depts.length === 0 && <p className="text-slate-500 col-span-3 text-center py-8">No departments created yet</p>}
      </div>

      {editDept && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Edit: {editDept.name}</h3>
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input value={editDept.name} onChange={e => setEditDept({...editDept, name: e.target.value})} /></div>
              <div><Label className="text-xs">Description</Label><Textarea value={editDept.description || ''} onChange={e => setEditDept({...editDept, description: e.target.value})} rows={2} /></div>
              <div><Label className="text-xs">Manager</Label>
                <Select value={editDept.manager_id || '_none'} onValueChange={v => setEditDept({...editDept, manager_id: v === '_none' ? null : v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="_none">None</SelectItem>{managers.map(m => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <Button variant="outline" onClick={() => setEditDept(null)} className="flex-1">Cancel</Button>
              <Button onClick={() => handleUpdate(editDept.id, { name: editDept.name, description: editDept.description, manager_id: editDept.manager_id })} className="flex-1 bg-lime-600 hover:bg-lime-700">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
